import { Fragment, useState, useRef, useEffect, useMemo } from "react";
import { Globe, Lock, ChevronDown, Check } from "lucide-react";
import { createPortal } from "react-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { LANGUAGES, getAvailableLanguages } from "@/lib/languages";
import { useTranslation } from "react-i18next";

const RECENT_KEY = "recent_languages";
const RECENT_MAX = 5;

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((c) => typeof c === "string") : [];
  } catch {
    return [];
  }
}

export function pushRecentLanguage(code: string) {
  try {
    const existing = readRecent().filter((c) => c !== code);
    const next = [code, ...existing].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

interface LanguageSelectorProps {
  isPaid?: boolean;
}

export function LanguageSelector({ isPaid = false }: LanguageSelectorProps) {
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [recent, setRecent] = useState<string[]>(() => readRecent());
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const listboxId = "language-listbox";

  const available = getAvailableLanguages(isPaid);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      return LANGUAGES.filter(
      (l) =>
        l.label.toLowerCase().includes(q) ||
        l.nativeLabel.toLowerCase().includes(q) ||
        l.code.toLowerCase().includes(q)
      );
    }
    // Recents first, then the rest (without duplicates).
    const recentLangs = recent
      .map((c) => LANGUAGES.find((l) => l.code === c))
      .filter((l): l is (typeof LANGUAGES)[number] => Boolean(l));
    const recentCodes = new Set(recentLangs.map((l) => l.code));
    const rest = LANGUAGES.filter((l) => !recentCodes.has(l.code));
    return [...recentLangs, ...rest];
  }, [query, recent]);

  const recentCount = query.trim() ? 0 : recent.filter((c) => LANGUAGES.some((l) => l.code === c)).length;

  const closeAndFocusButton = () => {
    setOpen(false);
    setQuery("");
    requestAnimationFrame(() => btnRef.current?.focus());
  };

  const selectAt = (idx: number) => {
    const lang = filtered[idx];
    if (!lang) return;
    if (!available.some((a) => a.code === lang.code)) return;
    setLanguage(lang.code);
    pushRecentLanguage(lang.code);
    setRecent(readRecent());
    closeAndFocusButton();
  };

  useEffect(() => {
    if (!open) return;
    const update = () => {
      const btn = btnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const isMobile = window.innerWidth < 480;
      const width = isMobile ? window.innerWidth - 16 : Math.min(320, window.innerWidth - 16);
      let left = r.right - width;
      if (left < 8) left = 8;
      if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
      setPos({ top: r.bottom + 6, left, width });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (portalRef.current?.contains(t)) return;
      closeAndFocusButton();
    };
    const touchHandler = (e: TouchEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (portalRef.current?.contains(t)) return;
      closeAndFocusButton();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", touchHandler, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", touchHandler);
    };
  }, [open]);

  // Refresh recents when opening (in case they were changed elsewhere).
  useEffect(() => {
    if (open) setRecent(readRecent());
  }, [open]);

  // Initialize active index to current selection when opening
  useEffect(() => {
    if (!open) return;
    const idx = filtered.findIndex((l) => l.code === language.code);
    setActiveIdx(idx >= 0 ? idx : 0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep active index in range when query changes
  useEffect(() => {
    setActiveIdx((i) => Math.min(Math.max(0, i), Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  // Scroll active option into view
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeAndFocusButton();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIdx(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIdx(filtered.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectAt(activeIdx);
    } else if (e.key === "Tab") {
      setOpen(false);
      setQuery("");
    }
  };

  const onButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        onKeyDown={onButtonKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={`${t("common.language")}: ${language.label}`}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-card border border-rule hover:bg-rule/30 transition-colors text-xs font-semibold text-ink"
      >
        <Globe className="size-3.5 text-ink-muted" />
        <span>{language.flag}</span>
        <span className="hidden sm:inline">{language.label}</span>
        <ChevronDown className="size-3 text-ink-muted" />
      </button>

      {open && pos && typeof document !== "undefined" && createPortal(
        <div
          ref={portalRef}
          className="fixed bg-card rounded-xl border border-rule shadow-2xl z-[100] overflow-hidden"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={onKeyDown}
          role="dialog"
          aria-label={t("common.language") as string}
        >
          <div className="px-3 py-2 border-b border-rule">
            <p className="text-[10px] uppercase tracking-wider text-ink-muted font-bold mb-1.5">
              {t("common.language")}
            </p>
            <input
              ref={inputRef}
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(0);
              }}
              placeholder={t("common.search")}
              aria-controls={listboxId}
              aria-activedescendant={filtered[activeIdx] ? `lang-opt-${filtered[activeIdx].code}` : undefined}
              className="w-full px-2 py-1.5 text-sm rounded-md bg-muted/40 border border-rule focus:outline-none focus:border-neon-pink text-ink"
            />
          </div>
          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label={t("common.language") as string}
            className="max-h-[min(360px,60vh)] overflow-y-auto py-1"
          >
          {recentCount > 0 && (
            <div
              className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-ink-muted font-bold"
              aria-hidden="true"
            >
              Recent
            </div>
          )}
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-xs text-ink-muted text-center">No matches</div>
          )}
          {filtered.map((lang, idx) => {
            const isAvailable = available.some((a) => a.code === lang.code);
            const isSelected = lang.code === language.code;
            const isActive = idx === activeIdx;
            const showDivider = recentCount > 0 && idx === recentCount;
            return (
              <Fragment key={lang.code}>
              {showDivider && (
                <div
                  className="px-3 pt-2 pb-0.5 mt-1 border-t border-rule text-[10px] uppercase tracking-wider text-ink-muted font-bold"
                  aria-hidden="true"
                >
                  All languages
                </div>
              )}
              <button
                id={`lang-opt-${lang.code}`}
                data-idx={idx}
                role="option"
                aria-selected={isSelected}
                aria-disabled={!isAvailable}
                aria-label={`${lang.label}${isSelected ? `, ${t("common.selected") ?? "selected"}` : ""}`}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => selectAt(idx)}
                disabled={!isAvailable}
                className={`w-full min-h-11 px-3 py-3 text-left text-sm flex items-center gap-3 transition-colors touch-manipulation ${
                  isSelected
                    ? "bg-neon-pink/10 text-neon-pink font-semibold"
                    : isActive && isAvailable
                    ? "bg-muted/60 text-ink"
                    : isAvailable
                    ? "text-ink hover:bg-muted/50"
                    : "text-ink-muted/50 cursor-not-allowed"
                }`}
              >
                <span className="text-lg leading-none">{lang.flag}</span>
                <span className="flex-1 flex flex-col items-start">
                  <span>{lang.nativeLabel}</span>
                  {lang.nativeLabel !== lang.label && (
                    <span className="text-[10px] text-ink-muted">{lang.label}</span>
                  )}
                </span>
                {lang.premium && !isPaid && <Lock className="size-3 text-ink-muted/50" />}
                {isSelected && <Check className="size-4 text-neon-pink" aria-label="Selected" />}
              </button>
              </Fragment>
            );
          })}
          </div>
          {/* Screen-reader live announcement for the active option */}
          <div className="sr-only" aria-live="polite" aria-atomic="true">
            {filtered[activeIdx]
              ? `${filtered[activeIdx].label}${filtered[activeIdx].code === language.code ? ", currently selected" : ""}`
              : ""}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
